import {
  type ChangeAcknowledgement,
  type ArtifactKind,
  type ChatStepSlug,
  type ArtifactStatus,
  type QuoteView,
  artifactKindSchema,
  artifactStatusSchema,
  audienceContentSchema,
  authorKindSchema,
  chatStepSlugSchema,
  provisioningStepSchema,
  quoteViewSchema,
  staleReasonSchema,
  reconciliationViewSchema,
  storedReadinessEscalationSchema,
  storedStudyProgressSchema,
  type StudyStepSlug,
  studyStateSchema,
  studyStepSlugSchema,
  studyStepViewSchema,
  type CategoryId,
  type StudyTypeId,
  type StudyTypeSelection,
  studyTypeSelectionSchema,
  type StoredStudyTypeSuggestion,
  storedStudyTypeSuggestionSchema,
  type FactId,
  type MaterialsInputs,
  type MaterialsRow,
  factIdSchema,
  materialsContentSchema,
  materialsShapeSchema,
  type StudyFileMeta,
  MAX_MATERIALS_FILE_BYTES,
  STUDY_FILE_MEDIA_TYPES,
  mebibytes,
  studyFileMetaSchema,
  goalContentSchema,
  goalOptionSetSchema,
  goalOptionsInputsSchema,
  goalOptionsRequestSchema,
  rqsContentSchema,
  rqsDraftInputsSchema,
  rqsDraftRequestSchema,
  stalenessWindowSchema,
  dgContentSchema,
  dgCoverageInputsSchema,
  dgCoverageRequestSchema,
  dgDraftInputsSchema,
  dgDraftRequestSchema,
  guideShapeSchema,
  audienceDraftInputsSchema,
  audienceDraftRequestSchema,
  LAUNCH_BLOCKER_ACTIONS,
  POLICY_REVIEW_OUTCOME_KINDS,
  POLICY_REVIEW_STATUSES,
  policyRefusalSchema,
  BRIEF_SYNTHESIS_REASONS,
  BRIEF_SYNTHESIS_STATUSES,
  briefContentSchema,
  type BriefEdit,
  FOLLOW_UP_STATES,
  FOLLOW_UP_UNAVAILABLE_REASONS,
  type FollowUpOutcome,
  type FollowUpOutcomeKind,
  type FollowUpRecord,
  type FollowUpView,
  followUpTextSchema,
  storedFollowUpAnswerSchema,
  parseArtifactVersionReason,
  parseBriefChangeNotes,
  PAYMENT_PROBLEM_KINDS,
  PURCHASE_FULFILLMENT_STATES,
  purchaseStateSchema,
} from '@sigora/domain';
import { z } from 'zod';

import type { SigoraUIMessage } from '../chat/events.ts';
import { ApiError, readApiError } from './errors.ts';
import { reportUnauthorized } from './unauthorized.ts';

/**
 * Every response is parsed with a schema rather than cast. The server and this bundle are built
 * from one repository but deployed as one process serving a cached bundle, so a user can be
 * running yesterday's SPA against today's API — and a contract change that renders blank is far
 * harder to diagnose than one that says which field moved.
 */
const sessionSchema = z.object({
  user: z.object({ id: z.uuid(), name: z.string(), email: z.string() }),
});
export type Session = z.infer<typeof sessionSchema>;

const studySummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  state: studyStateSchema,
  /**
   * When provisioning froze the study, or null. The list needs it because a study that is locked
   * and not yet launched is being set up: it cannot be deleted, and its state alone would read as
   * ready for work that is already under way.
   */
  lockedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  /**
   * Whether the server would accept a delete of this study right now. The list draws its delete
   * control on this alone rather than reasoning from the state: the server knows about the
   * checkout hold as well, and a control drawn without that would offer a delete it refuses.
   */
  deletable: z.boolean(),
});
export type StudySummary = z.infer<typeof studySummarySchema>;

/**
 * Where the run that carries a study into Remesh has got to, or null once there is no step being
 * done — a study that was never provisioned and one whose run finished alike. Those two read the
 * same here and the panel tells them apart by the study's own state.
 */
const studyProvisioningSchema = z.object({
  step: provisioningStepSchema,
  /** The step's place in the fixed order, counted from zero. */
  index: z.int().nonnegative(),
  total: z.int().positive(),
  /**
   * Whether the run stopped on this step because it threw, rather than being at work on it. The
   * server sends no reason with it: what the researcher can do about a stopped run is restart it,
   * and the reason is written for whoever reads the logs.
   */
  failed: z.boolean(),
});
export type StudyProvisioning = z.infer<typeof studyProvisioningSchema>;

/**
 * The purchase a payment status names, as far as Launch reads it: its state, the price it holds and
 * when it closed. Everything else about it stays on the server.
 */
const paymentPurchaseSchema = z.object({
  id: z.uuid(),
  state: purchaseStateSchema,
  quote: z.object({ total_cents: z.int().nonnegative(), currency: z.string().min(1) }),
  closedAt: z.iso.datetime().nullable(),
});

/**
 * A money fact about the study that no purchase state accounts for — a payment after its checkout
 * closed, one that disagreed with the purchase, a refund. An unresolved one is shown on Launch.
 */
const paymentProblemSchema = z.object({
  kind: z.enum(PAYMENT_PROBLEM_KINDS),
  createdAt: z.iso.datetime(),
  resolved: z.boolean(),
});
export type PaymentProblem = z.infer<typeof paymentProblemSchema>;

/** Where a paid study's launch stands, with the stop's code and a sentence of the server's. */
const fulfillmentSchema = z.object({
  state: z.enum(PURCHASE_FULFILLMENT_STATES),
  stop: z.object({ code: z.string(), message: z.string() }).nullable(),
});

/**
 * A study's payment, as the server reads it on every study read and answers every purchase call
 * with: `unpaid` (with the latest closed attempt, if any), `pending` (with whether its checkout
 * session is bound yet) or `paid` (with where its launch stands). `editingHeld` is what every step
 * reads to stop offering edits.
 */
const studyPaymentSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('unpaid'),
    purchase: paymentPurchaseSchema.nullable(),
    editingHeld: z.boolean(),
    checkout: z.null(),
    fulfillment: z.null(),
    problems: z.array(paymentProblemSchema),
  }),
  z.object({
    kind: z.literal('pending'),
    purchase: paymentPurchaseSchema,
    editingHeld: z.boolean(),
    checkout: z.enum(['opening', 'open']),
    fulfillment: z.null(),
    problems: z.array(paymentProblemSchema),
  }),
  z.object({
    kind: z.literal('paid'),
    purchase: paymentPurchaseSchema,
    editingHeld: z.boolean(),
    checkout: z.null(),
    fulfillment: fulfillmentSchema,
    problems: z.array(paymentProblemSchema),
  }),
]);
export type StudyPayment = z.infer<typeof studyPaymentSchema>;

const studySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  state: studyStateSchema,
  lockedAt: z.iso.datetime().nullable(),
  launchedAt: z.iso.datetime().nullable(),
  /** As on the summary: what the header decides whether to offer a delete from. */
  deletable: z.boolean(),
  /** Set at checkout, minutes before the lock — see the launch panel. */
  paidAt: z.iso.datetime().nullable(),
  /** Whether generated participants field this study rather than recruited ones. */
  isSimulation: z.boolean(),
  /**
   * What the progress poller last saw, or null while nothing has looked. The server polls
   * Remesh and writes this down; the panel renders the observation rather than asking for one.
   */
  remeshProgress: storedStudyProgressSchema.nullable(),
  /**
   * Why the readiness poller stopped waiting for an analysis, or null — which is both a study
   * still being analysed and one whose answers arrived.
   */
  readinessEscalation: storedReadinessEscalationSchema.nullable(),
  /** The set-up run's position while one is being worked through. See the schema above. */
  provisioning: studyProvisioningSchema.nullable(),
  /**
   * Where opening this study lands: the step it remembers, or its Brief once it is finished. The
   * server decides it, so the URL the shell corrects to and the step the study thinks it is on
   * cannot disagree.
   */
  currentStep: studyStepSlugSchema,
  /** All ten steps and where each one stands, in the order they happen in. */
  steps: z.array(studyStepViewSchema),
  /**
   * Which Study Type this study is and the template version it is pinned to, or null while nobody
   * has chosen one.
   *
   * The server resolves the version, so what arrives here is a study's own answer rather than the
   * catalogue's current one.
   */
  studyType: studyTypeSelectionSchema.nullable(),
  /**
   * The closest Study Type to the last sentence the researcher wrote, or null while they have
   * written none. Named here or the parse drops it, and the screen would wait on an answer it had
   * already been sent. A `ready` answer naming anything but one of the sixteen fails the parse, so
   * the screen can never be handed a type to offer that the catalogue does not hold.
   */
  studyTypeSuggestion: storedStudyTypeSuggestionSchema.nullable(),
  /** Where the study's payment stands, and whether a purchase holds its documents. */
  payment: studyPaymentSchema,
});
export type Study = z.infer<typeof studySchema> & {
  /**
   * When this read was asked for, on this page's clock, or absent on a study nobody read here.
   * Stamped by the client rather than sent by the server, and kept through a local change such as
   * a rename, so the panel can tell a read made after one of its requests answered from one that
   * was already out.
   */
  readAt?: number;
};

const artifactStateSchema = z.object({
  artifactId: z.uuid(),
  studyId: z.uuid(),
  kind: artifactKindSchema,
  currentVersion: z.int().positive(),
  approvedVersion: z.int().positive().nullable(),
  status: artifactStatusSchema,
  staleReason: staleReasonSchema.nullable(),
});
export type ArtifactStateRow = z.infer<typeof artifactStateSchema>;

const artifactCurrentSchema = z.object({
  artifactId: z.uuid(),
  kind: artifactKindSchema,
  version: z.int().positive(),
  approvedVersion: z.int().positive().nullable(),
  status: artifactStatusSchema,
  staleReason: staleReasonSchema.nullable(),
  authoredBy: authorKindSchema,
  content: z.unknown(),
});
export type ArtifactCurrent = z.infer<typeof artifactCurrentSchema>;

const artifactVersionSchema = z.object({
  version: z.int().positive(),
  authoredBy: authorKindSchema,
  contentHash: z.string(),
  createdAt: z.iso.datetime(),
});
export type ArtifactVersion = z.infer<typeof artifactVersionSchema>;

const writeResultSchema = z.object({
  artifactId: z.uuid(),
  kind: artifactKindSchema,
  version: z.int().positive(),
  status: artifactStatusSchema,
  deduped: z.boolean(),
  resolvedStaleness: z.boolean(),
});
export type WriteResult = z.infer<typeof writeResultSchema>;

const LOGIN_PATH = '/auth/login';

const request = async <T>(
  path: string,
  schema: z.ZodType<T>,
  init?: Omit<RequestInit, 'body'> & { body?: unknown },
): Promise<T> => {
  const { body, ...rest } = init ?? {};
  const response = await fetch(`/api${path}`, {
    ...rest,
    headers:
      body === undefined ? rest.headers : { 'content-type': 'application/json', ...rest.headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    // Reported before the error is thrown, so a caller that handles its own failures still cannot
    // swallow the one failure the whole app has to react to. Signing in is the exception: a
    // refused password is a 401 the form itself answers, and reporting it would be the app
    // telling itself to show the view it is already showing.
    if (path !== LOGIN_PATH) {
      reportUnauthorized(response.status);
    }

    throw await readApiError(response);
  }

  return schema.parse(await response.json());
};

export const fetchSession = async (): Promise<Session> => request('/session', sessionSchema);

const userSchema = z.object({ id: z.uuid(), name: z.string(), email: z.string() });
export type SignedInUser = z.infer<typeof userSchema>;

/**
 * Signs in, which sets the session cookie. Nothing is stored in this bundle: the cookie is
 * `HttpOnly`, so what the app knows about being signed in is only ever what a request answered.
 */
export const login = async (email: string, password: string): Promise<SignedInUser> =>
  (
    await request('/auth/login', z.object({ user: userSchema }), {
      method: 'POST',
      body: { email, password },
    })
  ).user;

/**
 * Signs out, which deletes the session server-side. Answers 204, so there is nothing to parse —
 * but a failure is still raised rather than assumed away: the session is only gone once the server
 * says so, and a caller that showed the login view regardless would be hiding a live session.
 */
export const logout = async (): Promise<void> => {
  const response = await fetch('/api/auth/logout', { method: 'POST' });

  if (!response.ok) {
    throw await readApiError(response);
  }
};

/**
 * What every signed-out request for mail answers, whatever the address: a signup, a resend and a
 * reset request are one response for an unknown address, an unconfirmed one and a confirmed one,
 * so nothing here can tell them apart and no screen should try.
 */
const checkEmailSchema = z.object({ status: z.literal('check_email') });

/** Creates an unconfirmed account, or mails the address that already has one. Never signs in. */
export const signUp = async (fields: {
  email: string;
  password: string;
  name: string;
}): Promise<void> => {
  await request('/auth/signup', checkEmailSchema, { method: 'POST', body: fields });
};

/**
 * Confirms the address a verification link was sent to. It does not sign in: the link proves the
 * mailbox, and the password is still what opens a session.
 */
export const verifyEmail = async (token: string): Promise<void> => {
  await request('/auth/verify-email', z.object({ status: z.literal('verified') }), {
    method: 'POST',
    body: { token },
  });
};

export const resendVerification = async (email: string): Promise<void> => {
  await request('/auth/verify-email/resend', checkEmailSchema, {
    method: 'POST',
    body: { email },
  });
};

export const requestPasswordReset = async (email: string): Promise<void> => {
  await request('/auth/password-reset', checkEmailSchema, { method: 'POST', body: { email } });
};

/**
 * Sets a new password from a reset link, which signs this browser in and every other one out: the
 * response sets a fresh session cookie, and every session the account had before is gone.
 */
export const confirmPasswordReset = async (
  token: string,
  password: string,
): Promise<SignedInUser> =>
  (
    await request('/auth/password-reset/confirm', z.object({ user: userSchema }), {
      method: 'POST',
      body: { token, password },
    })
  ).user;

/** Changes the signed-in user's display name, and nothing else about the account. */
export const updateDisplayName = async (name: string): Promise<SignedInUser> =>
  (
    await request('/account', z.object({ user: userSchema }), {
      method: 'PATCH',
      body: { name },
    })
  ).user;

export const renameStudy = async (studyId: string, title: string): Promise<StudySummary> =>
  (
    await request(`/studies/${studyId}`, z.object({ study: studySummarySchema }), {
      method: 'PATCH',
      body: { title },
    })
  ).study;

/**
 * Deletes the study, and with it its conversation and documents. Answers 204, so there is nothing
 * to parse — and like signing out, a failure is raised rather than assumed away: the refusals are
 * the whole point of this call, and the message they carry is written for the researcher.
 */
export const deleteStudy = async (studyId: string): Promise<void> => {
  const response = await fetch(`/api/studies/${studyId}`, { method: 'DELETE' });

  if (!response.ok) {
    reportUnauthorized(response.status);

    throw await readApiError(response);
  }
};

export const fetchStudies = async (): Promise<StudySummary[]> =>
  (await request('/studies', z.object({ studies: z.array(studySummarySchema) }))).studies;

export const createStudy = async (title?: string): Promise<string> =>
  (
    await request('/studies', z.object({ studyId: z.uuid() }), {
      method: 'POST',
      body: title === undefined ? {} : { title },
    })
  ).studyId;

/**
 * The transcript as the server shaped it: the parts are checked for the structural floor every UI
 * message part has and no further. Restating the SDK's part union here would be a second definition
 * of shapes this bundle does not own, and the one that drifted would refuse a conversation the chat
 * renders perfectly well — while the thing actually worth guarding, which tool parts reach a
 * browser at all, is decided on the server where the tools are.
 */
const transcriptMessageSchema = z.object({
  id: z.string().min(1),
  // Two roles rather than three: a `system` row is a briefing addressed to the agent, and the chat
  // would render one as a bubble labelled "Sigora". The server drops them, and this refuses one
  // rather than displaying it if that ever stops being true.
  role: z.enum(['user', 'assistant']),
  parts: z.array(z.looseObject({ type: z.string().min(1) })).min(1),
});

/**
 * The turn running in a study, as a thread's transcript read reports it: the same turn the server's
 * one-turn guard would refuse a send behind. `step` is the thread it is writing into, or null for a
 * turn whose row names none.
 */
const runningTurnSchema = z.object({ turnId: z.uuid(), step: chatStepSlugSchema.nullable() });
export type RunningTurn = z.infer<typeof runningTurnSchema>;

/**
 * One thread's transcript: the study and the step whose conversation it is, and the study's
 * running turn read in the same request. Another step's thread is another read, and nothing here
 * returns the study's conversation as a whole.
 *
 * The running turn comes with the rows rather than from a read of its own, so a turn cannot finish
 * between the two: a reply that is still being written is either in `running` or already a row.
 */
export const fetchMessages = async (
  studyId: string,
  step: ChatStepSlug,
): Promise<{ messages: SigoraUIMessage[]; running: RunningTurn | null }> => {
  const thread = await request(
    `/studies/${studyId}/threads/${step}/messages`,
    z.object({
      messages: z.array(transcriptMessageSchema),
      running: runningTurnSchema.nullable(),
    }),
  );

  return { messages: thread.messages as unknown as SigoraUIMessage[], running: thread.running };
};

/**
 * The panel's demographics, as words. Ids are what an audience document stores and labels are what
 * the researcher asked for, so this is the only thing standing between the card and
 * "household-income-usd-us-participants-only: 8, 9".
 *
 * `available` is carried rather than dropped because "the catalog says nothing about this filter"
 * and "there was no catalog to ask" look identical at the filter and are not the same fact. The
 * card renders raw either way; only what it can honestly claim about the gap differs.
 */
const audienceCatalogSchema = z.object({
  available: z.boolean(),
  dimensions: z.array(
    z.object({
      id: z.string().min(1),
      label: z.string().min(1),
      question: z.string(),
      options: z.array(z.object({ value: z.string(), label: z.string() })),
    }),
  ),
});
export type AudienceCatalog = z.infer<typeof audienceCatalogSchema>;

export const fetchAudienceCatalog = async (studyId: string): Promise<AudienceCatalog> =>
  (
    await request(
      `/studies/${studyId}/audience-catalog`,
      z.object({ catalog: audienceCatalogSchema }),
    )
  ).catalog;

export const fetchStudy = async (studyId: string): Promise<Study> => {
  const readAt = Date.now();

  return { ...(await request(`/studies/${studyId}`, studySchema)), readAt };
};

/** Remember where the researcher has got to. Refused for a step the study has not reached. */
export const setStudyStep = async (studyId: string, step: StudyStepSlug): Promise<StudyStepSlug> =>
  (
    await request(`/studies/${studyId}/step`, z.object({ currentStep: studyStepSlugSchema }), {
      method: 'PATCH',
      body: { step },
    })
  ).currentStep;

/**
 * Record which Study Type this study is. `categoryId` is the Category a Custom study was started
 * from; a named type takes its Category from the catalogue and is refused one that disagrees.
 */
export const setStudyType = async (
  studyId: string,
  studyTypeId: StudyTypeId,
  categoryId: CategoryId | undefined,
  acknowledgedDownstream: ChangeAcknowledgement | null,
): Promise<StudyTypeSelection> =>
  (
    await request(
      `/studies/${studyId}/study-type`,
      z.object({ studyType: studyTypeSelectionSchema }),
      { method: 'PUT', body: { studyTypeId, categoryId, acknowledgedDownstream } },
    )
  ).studyType;

/**
 * Asks for the closest Study Type to a sentence. The answer is not in the response: the request is
 * stored and answered by the worker, and the study read carries the answer once it has been
 * written — so what comes back here is the request as stored, normally still `pending`.
 */
export const suggestStudyType = async (
  studyId: string,
  text: string,
): Promise<StoredStudyTypeSuggestion> =>
  (
    await request(
      `/studies/${studyId}/study-type/suggestion`,
      z.object({ studyTypeSuggestion: storedStudyTypeSuggestionSchema }),
      { method: 'POST', body: { text } },
    )
  ).studyTypeSuggestion;

const materialsRowSchema = z.object({
  factId: factIdSchema,
  label: z.string(),
  required: z.boolean(),
  value: z.string().nullable(),
  source: z.enum(['input', 'extracted', 'user']),
  state: z.enum(['found', 'none_found', 'none_found_in_read', 'not_read', 'changed']),
}) satisfies z.ZodType<MaterialsRow>;
export type MaterialsRowView = z.infer<typeof materialsRowSchema>;

/**
 * Everything the Materials step shows, as the server decided it: what the Study Type asks for now,
 * the version the study holds with its rows, what is missing, and whether it may be or already is
 * confirmed. The screen renders these answers rather than working them out again.
 */
const materialsViewSchema = z.object({
  /** What the study's Study Type asks for now, or null while it has none. */
  shape: materialsShapeSchema.nullable(),
  editable: z.boolean(),
  current: z
    .object({
      artifactId: z.uuid(),
      version: z.int().positive(),
      approvedVersion: z.int().positive().nullable(),
      status: artifactStatusSchema,
      authoredBy: authorKindSchema,
      content: materialsContentSchema,
    })
    .nullable(),
  rows: z.array(materialsRowSchema),
  missing: z.array(z.object({ factId: factIdSchema, label: z.string(), request: z.string() })),
  confirmable: z.boolean(),
  confirmedCurrent: z.boolean(),
  /** The files the current version names, in input order. */
  files: z.array(studyFileMetaSchema),
});
export type MaterialsView = z.infer<typeof materialsViewSchema>;

/**
 * Every version of the Materials, newest first: who made it, whether it is the current version
 * confirmed now, and the files it names, each opened through `materialsFileUrl`. Read on its own,
 * when the step's history is opened.
 */
const materialsHistorySchema = z.object({
  history: z.array(
    z.object({
      version: z.int().positive(),
      authoredBy: authorKindSchema,
      confirmed: z.boolean(),
      files: z.array(z.object({ id: z.uuid(), name: z.string() })),
      createdAt: z.iso.datetime(),
    }),
  ),
});
export type MaterialsHistory = z.infer<typeof materialsHistorySchema>['history'];

export const fetchMaterialsHistory = async (
  studyId: string,
  signal?: AbortSignal,
): Promise<MaterialsHistory> =>
  (await request(`/studies/${studyId}/materials/history`, materialsHistorySchema, { signal }))
    .history;

const materialsResponseSchema = z.object({ materials: materialsViewSchema });

export const fetchMaterials = async (
  studyId: string,
  signal?: AbortSignal,
): Promise<MaterialsView> =>
  (await request(`/studies/${studyId}/materials`, materialsResponseSchema, { signal })).materials;

/**
 * Saves what the researcher added, naming the version they were looking at — `0` for none — so a
 * save made over Materials that moved meanwhile is refused rather than written over them. Answers
 * with the step as it now stands, the reading normally still pending.
 */
export const saveMaterials = async ({
  studyId,
  inputs,
  expectedVersion,
  acknowledgedDownstream,
}: {
  studyId: string;
  inputs: MaterialsInputs;
  expectedVersion: number;
  acknowledgedDownstream: ChangeAcknowledgement | null;
}): Promise<MaterialsView> =>
  (
    await request(`/studies/${studyId}/materials`, materialsResponseSchema, {
      method: 'PUT',
      body: { inputs, expectedVersion, acknowledgedDownstream },
    })
  ).materials;

/**
 * Fixes one row, or says there is nothing for it with `null`. Applied to whatever version is current
 * when it lands, so a neighbouring fix or a reading that settled meanwhile is kept; `baseVersion` is
 * the version the row was fixed on, which the server records.
 */
export const correctMaterialsFact = async ({
  studyId,
  factId,
  value,
  baseVersion,
  acknowledgedDownstream,
}: {
  studyId: string;
  factId: FactId;
  value: string | null;
  baseVersion: number;
  acknowledgedDownstream: ChangeAcknowledgement | null;
}): Promise<MaterialsView> =>
  (
    await request(
      `/studies/${studyId}/materials/facts/${encodeURIComponent(factId)}`,
      materialsResponseSchema,
      { method: 'PATCH', body: { value, baseVersion, acknowledgedDownstream } },
    )
  ).materials;

const uploadResponseSchema = z.object({ file: studyFileMetaSchema });

/** How far an upload has got, in bytes of the file sent. */
export type UploadProgress = (sent: number, total: number) => void;

/** What an upload answered: the file as stored, and whether these bytes were new to the study. */
export interface UploadedFile {
  file: StudyFileMeta;
  created: boolean;
}

/**
 * The one refusal of an upload answered by whatever stands in front of the API as much as by the
 * API itself: a proxy's size limit answers 413 with a page of its own, which says nothing a reader
 * can use, so every 413 is told the limit the API holds files to.
 */
const fileTooLarge = (): ApiError =>
  new ApiError(
    'file_too_large',
    `Files can be up to ${mebibytes(MAX_MATERIALS_FILE_BYTES)}.`,
    413,
    {},
  );

/**
 * An upload that stopped before the server answered — a dropped connection. The only failure of an
 * upload that sending the same file again can fix without anything else changing, together with a
 * server fault and a file that arrived damaged.
 */
export class UploadInterruptedError extends Error {
  constructor() {
    super('The upload stopped before it finished. Try again.');
    this.name = 'UploadInterruptedError';
  }
}

/** A refusal read from an upload's answer; an answer that cannot be read is still a refusal. */
const uploadRefusal = async (status: number, body: string): Promise<ApiError> => {
  try {
    return await readApiError(new Response(body, { status }));
  } catch {
    return new ApiError('request_failed', 'Something went wrong. Please try again.', status, {});
  }
};

/**
 * Sends one file for the study to keep, as the request body itself, and answers with the file as
 * the server stored it. That can differ from what was picked: the bytes decide the type, and bytes
 * the study already holds answer with the file they were first stored as, name included.
 *
 * Over XMLHttpRequest rather than `fetch`, because only it reports how much of a body has been
 * sent. An upload that stops before the server answers — a dropped connection — rejects with an
 * `UploadInterruptedError`, and one given up on through `signal` with an `AbortError`. A refusal
 * rejects with an `ApiError`, and an answer that is not a stored file with a plain `Error`, which
 * sending the file again would not change.
 */
export const uploadMaterialsFile = (
  studyId: string,
  file: File,
  onProgress: UploadProgress,
  signal?: AbortSignal,
): Promise<UploadedFile> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();

    const finish = () => signal?.removeEventListener('abort', abort);

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    });
    xhr.addEventListener('load', () => {
      finish();
      reportUnauthorized(xhr.status);

      if (xhr.status === 413) {
        reject(fileTooLarge());
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        void uploadRefusal(xhr.status, xhr.responseText).then(reject, reject);
        return;
      }

      // An answer that is not a stored file is a contract this page no longer shares with the
      // server, which sending the file again would not change.
      let parsed: ReturnType<typeof uploadResponseSchema.safeParse> | null = null;
      try {
        parsed = uploadResponseSchema.safeParse(JSON.parse(xhr.responseText) as unknown);
      } catch {
        parsed = null;
      }

      if (parsed?.success === true) {
        resolve({ file: parsed.data.file, created: xhr.status === 201 });
      } else {
        reject(
          new Error(
            'The file was uploaded, but Sigora’s answer could not be read. Reload the page.',
          ),
        );
      }
    });
    xhr.addEventListener('error', () => {
      finish();
      reject(new UploadInterruptedError());
    });
    xhr.addEventListener('abort', () => {
      finish();
      reject(new DOMException('The upload was stopped.', 'AbortError'));
    });

    if (signal?.aborted === true) {
      reject(new DOMException('The upload was stopped.', 'AbortError'));
      return;
    }

    signal?.addEventListener('abort', abort);
    xhr.open(
      'POST',
      `/api/studies/${studyId}/materials/files?name=${encodeURIComponent(file.name)}`,
    );
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.send(file);
  });

/** Where a study's stored file is read from; an image's answer can be shown as it is. */
export const materialsFileUrl = (studyId: string, fileId: string): string =>
  `/api/studies/${studyId}/materials/files/${fileId}`;

/**
 * Says whether participants may be shown an image the Materials hold. The answer is a new version,
 * which keeps the reading and needs confirming again; `expectedVersion` is the version the choice
 * was made on, and one that moved meanwhile is refused.
 */
export const setMaterialsStimulus = async ({
  studyId,
  fileId,
  candidate,
  expectedVersion,
  acknowledgedDownstream,
}: {
  studyId: string;
  fileId: string;
  candidate: boolean;
  expectedVersion: number;
  acknowledgedDownstream: ChangeAcknowledgement | null;
}): Promise<MaterialsView> =>
  (
    await request(`/studies/${studyId}/materials/stimulus`, materialsResponseSchema, {
      method: 'PATCH',
      body: { fileId, candidate, expectedVersion, acknowledgedDownstream },
    })
  ).materials;

/** The options on the Goal card and the request behind them; null while none was ever asked for. */
const goalOptionsViewSchema = z
  .object({
    request: goalOptionsRequestSchema.nullable(),
    set: goalOptionSetSchema.nullable(),
  })
  .nullable();
export type GoalOptionsView = z.infer<typeof goalOptionsViewSchema>;

/**
 * Everything the Goal step shows, as the server holds it: the options on the card, what the study
 * would draft from now (null while its Materials are not confirmed), and the chosen Goal with every
 * version and why each exists.
 */
const goalViewSchema = z.object({
  options: goalOptionsViewSchema,
  currentInputs: goalOptionsInputsSchema.nullable(),
  goal: z
    .object({
      artifactId: z.uuid(),
      version: z.int().positive(),
      status: artifactStatusSchema,
      content: goalContentSchema,
    })
    .nullable(),
  history: z.array(
    z.object({
      version: z.int().positive(),
      reason: z.string(),
      chosenTitle: z.string(),
      createdAt: z.iso.datetime(),
    }),
  ),
});
export type GoalView = z.infer<typeof goalViewSchema>;

export const fetchGoal = async (studyId: string, signal?: AbortSignal): Promise<GoalView> =>
  request(`/studies/${studyId}/goal`, goalViewSchema, { signal });

/**
 * Asks for drafted goal options. Without `again` the server answers what it holds when a request
 * for the study as it stands is in hand or a set already describes it; `again` asks regardless,
 * which is "Try again". Answers with what was stored, the request normally still pending.
 */
export const requestGoalOptions = async (
  studyId: string,
  { again = false }: { again?: boolean } = {},
): Promise<GoalOptionsView> =>
  (
    await request(
      `/studies/${studyId}/goal/options`,
      z.object({ options: goalOptionsViewSchema }),
      {
        method: 'POST',
        body: again ? { again: true } : {},
      },
    )
  ).options;

/**
 * "Use this goal": names the set and the option on screen and the Goal version the screen showed
 * (null before one was chosen), so a choice made over options or a Goal that moved is refused.
 * `acknowledgedDownstream` is what the researcher agreed a change does below the Goal, from the
 * confirm they answered (`acknowledgementOf`), or `null` before they have been asked.
 */
export const selectGoal = async ({
  studyId,
  optionSetId,
  optionId,
  expectedVersion,
  acknowledgedDownstream,
}: {
  studyId: string;
  optionSetId: string;
  optionId: string;
  expectedVersion: number | null;
  acknowledgedDownstream: ChangeAcknowledgement | null;
}): Promise<{ version: number; deduped: boolean }> =>
  request(
    `/studies/${studyId}/goal/select`,
    z.object({ version: z.int().positive(), deduped: z.boolean() }),
    {
      method: 'POST',
      body: {
        option_set_id: optionSetId,
        option_id: optionId,
        expected_version: expectedVersion,
        acknowledged_downstream: acknowledgedDownstream,
      },
    },
  );

/**
 * Everything the Questions step shows, as the server holds it: the research questions and where
 * they stand, every version with what produced it (oldest first), what a reconciled current version
 * changed and why, the latest drafting request, what the study would draft from now (null while its Materials are not confirmed or its Goal is not
 * chosen), how many questions its template starts with, and the Goal they decompose.
 */
const questionsViewSchema = z.object({
  rqs: z
    .object({
      artifactId: z.uuid(),
      version: z.int().positive(),
      status: artifactStatusSchema,
      approvedVersion: z.int().positive().nullable(),
      staleness: z.array(stalenessWindowSchema),
      content: rqsContentSchema,
    })
    .nullable(),
  history: z.array(
    z.object({
      version: z.int().positive(),
      authoredBy: authorKindSchema,
      reason: z.string().nullable(),
      reasonText: z.string(),
      createdAt: z.iso.datetime(),
    }),
  ),
  /** What a reconciled current version changed and why; `null` for any other version. */
  reconciliation: reconciliationViewSchema.nullable(),
  draft: rqsDraftRequestSchema.nullable(),
  currentInputs: rqsDraftInputsSchema.nullable(),
  intendedCount: z.int().positive(),
  goal: z
    .object({ version: z.int().positive(), title: z.string(), decision: z.string() })
    .nullable(),
});
export type QuestionsView = z.infer<typeof questionsViewSchema>;

export const fetchQuestions = async (
  studyId: string,
  signal?: AbortSignal,
): Promise<QuestionsView> =>
  request(`/studies/${studyId}/questions`, questionsViewSchema, { signal });

/**
 * Asks for the research questions to be drafted. Without `again` the server answers what it holds
 * when the questions are current or a request for the study as it stands is in hand; `again` is
 * "Try again". Answers with the request as stored, normally still pending, or null when nothing
 * needs drafting.
 */
export const requestQuestionsDraft = async (
  studyId: string,
  { again = false }: { again?: boolean } = {},
): Promise<QuestionsView['draft']> =>
  (
    await request(
      `/studies/${studyId}/questions/draft`,
      z.object({ draft: rqsDraftRequestSchema.nullable() }),
      { method: 'POST', body: again ? { again: true } : {} },
    )
  ).draft;

/**
 * Rewording one research question. Names the version of the set on screen, so a reword made over
 * questions that moved is refused rather than written over them.
 */
export const rewordQuestion = async ({
  studyId,
  rqId,
  text,
  expectedVersion,
  acknowledgedDownstream,
}: {
  studyId: string;
  rqId: string;
  text: string;
  expectedVersion: number;
  acknowledgedDownstream: ChangeAcknowledgement | null;
}): Promise<{ version: number; deduped: boolean }> =>
  request(
    `/studies/${studyId}/questions/${rqId}`,
    z.object({ version: z.int().positive(), deduped: z.boolean() }),
    {
      method: 'PATCH',
      body: {
        text,
        expected_version: expectedVersion,
        acknowledged_downstream: acknowledgedDownstream,
      },
    },
  );

/**
 * The price Launch shows: a live issued quote, issued now or answered as already standing, or the
 * price a purchase or the lock already holds the study at (`accepted: true`). A POST because it may
 * issue one; asking again inside its window answers the same quote.
 */
export const issueQuote = async (studyId: string): Promise<QuoteView> =>
  request(`/studies/${studyId}/quote`, quoteViewSchema, { method: 'POST' });

/**
 * What the browser mounts the embedded checkout with while a session is open. The client secret is
 * read from the provider on each request and kept nowhere; the fake's publishable key is null.
 */
const checkoutCredentialsSchema = z.object({
  clientSecret: z.string().min(1),
  publishableKey: z.string().min(1).nullable(),
});
export type CheckoutCredentials = z.infer<typeof checkoutCredentialsSchema>;

/**
 * A purchase's payment status with its checkout credentials, which are null for a purchase that is
 * not pending and for one whose session could not be opened yet (`status.checkout` is `opening`).
 */
const checkoutAnswerSchema = z.object({
  status: studyPaymentSchema,
  checkout: checkoutCredentialsSchema.nullable(),
});
export type CheckoutAnswer = z.infer<typeof checkoutAnswerSchema>;

/**
 * Opens a purchase at the issued quote on screen, named by its id, and its checkout session — or
 * answers with the purchase already holding the study. Nothing is charged here; the payment is
 * taken in the provider's form and confirmed by the provider.
 */
export const createPurchase = async (studyId: string, quoteId: string): Promise<CheckoutAnswer> =>
  request(`/studies/${studyId}/purchases`, checkoutAnswerSchema, {
    method: 'POST',
    body: { quote_id: quoteId },
  });

/**
 * The pending purchase's checkout: its session opened now when it has none yet, or read again. A
 * session that completed or expired is settled first and answered with no credentials.
 */
export const openCheckout = async (studyId: string): Promise<CheckoutAnswer> =>
  request(`/studies/${studyId}/purchase/checkout`, checkoutAnswerSchema, {
    method: 'POST',
    body: {},
  });

/**
 * Cancels the pending checkout. The session is closed before the hold is released, so a payment
 * that completed first stands and the answer is `paid`.
 */
export const cancelCheckout = async (studyId: string): Promise<StudyPayment> =>
  request(`/studies/${studyId}/purchase/cancel`, studyPaymentSchema, { method: 'POST', body: {} });

/**
 * Asks the server to settle the pending purchase from what the provider reports now. It sends
 * nothing about the payment, so nothing the browser says can confirm one.
 */
export const refreshPurchase = async (studyId: string): Promise<StudyPayment> =>
  request(`/studies/${studyId}/purchase/refresh`, studyPaymentSchema, { method: 'POST', body: {} });

const fakeCheckoutEventSchema = z.object({ eventId: z.string(), purchaseId: z.uuid() });

/**
 * Pays or expires the study's open checkout through the credential-free fake provider, as Stripe's
 * form or its expiry would: the server signs the event and posts it through its own webhook, and
 * the worker settles the purchase. The route exists only on a local stack running the fake.
 */
export const sendFakeCheckoutEvent = async (
  studyId: string,
  action: 'complete' | 'expire',
): Promise<void> => {
  await request(`/dev/payments/fake/${studyId}/${action}`, fakeCheckoutEventSchema, {
    method: 'POST',
  });
};

/**
 * Starts the setup of a study already paid for again. It agrees to no price and takes no payment;
 * the answer is the payment status, with the launch queued again.
 */
export const retrySetup = async (studyId: string): Promise<StudyPayment> =>
  request(`/studies/${studyId}/setup/retry`, studyPaymentSchema, { method: 'POST', body: {} });

const policyReviewViewSchema = z.object({
  status: z.enum(POLICY_REVIEW_STATUSES).nullable(),
  outcome: z.enum(POLICY_REVIEW_OUTCOME_KINDS),
  refusals: z.array(policyRefusalSchema),
  requestedAt: z.iso.datetime().nullable(),
});
export type PolicyReviewView = z.infer<typeof policyReviewViewSchema>;

/**
 * One blocker. `kind` is read as any string: a server newer than this bundle may name a kind it
 * does not know, and the row still says its message and offers its action. `code`, `step` and
 * `action` are what the panel acts on, and are held to their lists.
 */
const launchBlockerSchema = z.object({
  kind: z.string(),
  code: z.string(),
  step: studyStepSlugSchema.nullable(),
  message: z.string(),
  action: z.enum(LAUNCH_BLOCKER_ACTIONS),
});
export type LaunchBlocker = z.infer<typeof launchBlockerSchema>;

/**
 * Every reason the study cannot be paid for yet, each with the step that fixes it, and the policy
 * review as it stands for the study now (`null` until the study can be reviewed).
 */
const launchCheckSchema = z.object({
  blockers: z.array(launchBlockerSchema),
  policyReview: policyReviewViewSchema.nullable(),
  reviewSubject: z.string().nullable(),
});
export type LaunchCheck = z.infer<typeof launchCheckSchema>;

export const fetchLaunchCheck = async (studyId: string): Promise<LaunchCheck> =>
  request(`/studies/${studyId}/launch/check`, launchCheckSchema);

/**
 * Asks for the study to be reviewed as it stands, which Launch does on arrival; `retry` is "Check
 * again", which asks again only over a review that could not be had or read.
 */
export const requestPolicyReview = async (
  studyId: string,
  { retry }: { retry: boolean },
): Promise<PolicyReviewView> =>
  request(`/studies/${studyId}/launch/review`, policyReviewViewSchema, {
    method: 'POST',
    body: { retry },
  });

/** The policy review as it stands: the light read Launch polls while a review is pending. */
export const fetchPolicyReview = async (studyId: string): Promise<PolicyReviewView> =>
  request(`/studies/${studyId}/launch/review`, policyReviewViewSchema);

/**
 * The latest request to write the study's Brief, as the Fielding step reads it. `retryable` is
 * the server's answer to whether Try again is offered.
 */
const briefSynthesisViewSchema = z.object({
  status: z.enum(BRIEF_SYNTHESIS_STATUSES),
  reason: z.enum(BRIEF_SYNTHESIS_REASONS).nullable(),
  requestedAt: z.iso.datetime(),
  settledAt: z.iso.datetime().nullable(),
  attempt: z.int().positive(),
  retryable: z.boolean(),
});
export type BriefSynthesisView = z.infer<typeof briefSynthesisViewSchema>;

/**
 * A Brief version's reason and change notes, read the way the server reads them: a reason or notes
 * this bundle does not know are `null` rather than a failed read, so the version still opens and
 * its history line falls back to who wrote it.
 */
const briefVersionReasonSchema = z.unknown().transform(parseArtifactVersionReason);
const briefChangeNotesViewSchema = z.unknown().transform(parseBriefChangeNotes);

/** One line of the Brief's history: a version, who wrote it, why and when, and its change notes. */
const briefHistoryEntrySchema = z.object({
  version: z.int().positive(),
  authoredBy: authorKindSchema,
  reason: briefVersionReasonSchema,
  createdAt: z.iso.datetime(),
  notes: briefChangeNotesViewSchema,
});
export type BriefHistoryEntryView = z.infer<typeof briefHistoryEntrySchema>;

/** How a follow-up landed: a note under a card, a log line, or an update of the Brief. */
const followUpOutcomeSchema: z.ZodType<FollowUpOutcome> = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('note'), rqId: z.uuid() }),
  z.object({ kind: z.literal('log') }),
  z.object({
    kind: z.literal('update_brief'),
    briefVersion: z.int().positive(),
    changed: z.boolean(),
  }),
]);

/**
 * What the Brief's finalized follow-ups add to it as it stands now: the notes under its cards, the
 * Appendix log of every one oldest first, each with how it landed, and how many there are.
 */
const followUpRecordSchema: z.ZodType<FollowUpRecord> = z.object({
  notes: z.array(
    z.object({
      followUpId: z.uuid(),
      rqId: z.uuid(),
      finalizedAt: z.iso.datetime(),
      question: z.string(),
      text: followUpTextSchema,
      quotes: z.array(
        z.object({
          quote_id: z.string().min(1),
          participant: z.string().min(1),
          text: z.string().min(1),
        }),
      ),
    }),
  ),
  log: z.array(
    z.object({
      followUpId: z.uuid(),
      question: z.string(),
      finalizedAt: z.iso.datetime(),
      outcome: followUpOutcomeSchema,
    }),
  ),
  followUpCount: z.int().nonnegative(),
});

/**
 * The study's state, its latest request to write the Brief, and the Brief itself once the study
 * is complete: its current version, who wrote it and when, its content, every version's history
 * line (oldest first, the current one last), Sigora's newest revision, if there is one, and the
 * record its follow-ups left, read with the version. The content is parsed with the schema it was
 * stored under, so a Brief this bundle cannot read is a failed read rather than a page drawn from
 * half of it.
 */
const briefReadSchema = z.object({
  state: studyStateSchema,
  synthesis: briefSynthesisViewSchema.nullable(),
  brief: z
    .object({
      version: z.int().positive(),
      content: briefContentSchema,
      createdAt: z.iso.datetime(),
      authoredBy: authorKindSchema,
      history: z.array(briefHistoryEntrySchema).min(1),
      lastRevision: z
        .object({
          version: z.int().positive(),
          createdAt: z.iso.datetime(),
          notes: briefChangeNotesViewSchema,
        })
        .nullable(),
      record: followUpRecordSchema,
    })
    .nullable(),
});
export type BriefReadView = z.infer<typeof briefReadSchema>;

export const fetchBrief = async (studyId: string, signal?: AbortSignal): Promise<BriefReadView> =>
  request(`/studies/${studyId}/brief`, briefReadSchema, { signal });

/**
 * One framing edit to the published Brief: a Verdict reworded, a summary bullet hidden or shown, or
 * the recommendations reordered. Names the version on screen, so an edit made over a Brief that
 * moved is refused rather than written over it, and answers with the Brief as it stands after the
 * edit, which replaces the read on screen.
 */
export const editBrief = async ({
  studyId,
  edit,
  expectedVersion,
}: {
  studyId: string;
  edit: BriefEdit;
  expectedVersion: number;
}): Promise<{ deduped: boolean; read: BriefReadView }> =>
  request(`/studies/${studyId}/brief`, z.object({ deduped: z.boolean(), read: briefReadSchema }), {
    method: 'PATCH',
    body: { expected_version: expectedVersion, edit },
  });

const briefVersionReadSchema = briefHistoryEntrySchema.extend({ content: briefContentSchema });
export type BriefVersionView = z.infer<typeof briefVersionReadSchema>;

/** One version of the study's published Brief, current or not, with its history line. */
export const fetchBriefVersion = async (
  studyId: string,
  version: number,
  signal?: AbortSignal,
): Promise<BriefVersionView> =>
  request(`/studies/${studyId}/brief/versions/${String(version)}`, briefVersionReadSchema, {
    signal,
  });

const followUpFailureReasonSchema = z.enum([...FOLLOW_UP_UNAVAILABLE_REASONS, 'unreadable']);

/**
 * One follow-up as the Brief's thread shows it: the question, where it stands, its answer once it
 * has one, why it has none or why its update failed, whether Try again is offered, and how it landed
 * once it has.
 */
const followUpViewSchema: z.ZodType<FollowUpView> = z.object({
  id: z.uuid(),
  question: z.string(),
  rqId: z.uuid().nullable(),
  askedAt: z.iso.datetime(),
  briefVersion: z.int().positive(),
  state: z.enum(FOLLOW_UP_STATES),
  answer: storedFollowUpAnswerSchema.nullable(),
  // A reason this build does not know fails no read of the thread. An answer reads as one that
  // failed with no reason given; an update as one that failed, in the line every reason but the
  // budget gets.
  answerReason: followUpFailureReasonSchema.nullable().catch(null),
  retryable: z.boolean(),
  updateReason: followUpFailureReasonSchema.nullable().catch('unreadable'),
  updateRetryable: z.boolean(),
  outcome: followUpOutcomeSchema.nullable(),
});

/** The study's follow-ups, oldest first. */
export const fetchFollowUps = async (
  studyId: string,
  signal?: AbortSignal,
): Promise<FollowUpView[]> =>
  (
    await request(
      `/studies/${studyId}/brief/follow-ups`,
      z.object({ followUps: z.array(followUpViewSchema) }),
      { signal },
    )
  ).followUps;

/**
 * Asks a follow-up question about the published Brief, under an id this tab minted for it: sent
 * again with the same id and question, it is answered with the follow-up already stored rather
 * than asked twice. `rqId` names the card it is about, or is `null` for the whole study.
 */
export const askFollowUp = async ({
  studyId,
  id,
  question,
  rqId,
}: {
  studyId: string;
  id: string;
  question: string;
  rqId: string | null;
}): Promise<{ deduped: boolean; followUp: FollowUpView }> =>
  request(
    `/studies/${studyId}/brief/follow-ups`,
    z.object({ deduped: z.boolean(), followUp: followUpViewSchema }),
    { method: 'POST', body: { id, question, rq_id: rqId } },
  );

/** Try again: asks for a follow-up's answer again after one Sigora could not give. */
export const retryFollowUp = async (studyId: string, followUpId: string): Promise<FollowUpView> =>
  (
    await request(
      `/studies/${studyId}/brief/follow-ups/${followUpId}/retry`,
      z.object({ followUp: followUpViewSchema }),
      { method: 'POST' },
    )
  ).followUp;

/**
 * How an answered follow-up lands. A note or a log line answers with the Brief as it left it
 * (`read`); an update of the Brief answers before it is written, with no read, and the follow-up
 * reads `updating` until it is. `rqId` names the card a note goes under, only where the question
 * and its answer name none.
 */
export const chooseFollowUpOutcome = async ({
  studyId,
  followUpId,
  kind,
  rqId,
}: {
  studyId: string;
  followUpId: string;
  kind: FollowUpOutcomeKind;
  rqId: string | null;
}): Promise<{ deduped: boolean; followUp: FollowUpView; read: BriefReadView | null }> =>
  request(
    `/studies/${studyId}/brief/follow-ups/${followUpId}/outcome`,
    z.object({
      deduped: z.boolean(),
      followUp: followUpViewSchema,
      read: briefReadSchema.nullable().default(null),
    }),
    { method: 'POST', body: kind === 'note' ? { kind, rq_id: rqId } : { kind } },
  );

/** Try again: asks for the Brief to be written again after a request that could not be. */
export const retryBrief = async (studyId: string): Promise<BriefReadView> =>
  request(`/studies/${studyId}/brief/retry`, briefReadSchema, { method: 'POST' });

export const fetchArtifactState = async (studyId: string): Promise<ArtifactStateRow[]> =>
  (
    await request(
      `/studies/${studyId}/artifacts`,
      z.object({ artifacts: z.array(artifactStateSchema) }),
    )
  ).artifacts;

export const fetchArtifact = async (
  studyId: string,
  kind: ArtifactKind,
): Promise<ArtifactCurrent> =>
  request(`/studies/${studyId}/artifacts/kind/${kind}`, artifactCurrentSchema);

/**
 * Reading one artifact by its own id, which is the only correct way to read an answer: a study
 * has one artifact of each chain kind but as many answers as it has research questions, so the
 * by-kind route above would return an arbitrary one of them and hide the rest.
 */
export const fetchArtifactById = async (
  studyId: string,
  artifactId: string,
): Promise<ArtifactCurrent> =>
  request(`/studies/${studyId}/artifacts/${artifactId}`, artifactCurrentSchema);

export const fetchArtifactHistory = async (
  studyId: string,
  artifactId: string,
): Promise<ArtifactVersion[]> =>
  (
    await request(
      `/studies/${studyId}/artifacts/${artifactId}/history`,
      z.object({ versions: z.array(artifactVersionSchema) }),
    )
  ).versions;

/**
 * Approving names the version being approved, so an approval cannot land on something the user
 * never saw: the agent rewriting an artifact between the render and the click is refused rather
 * than silently endorsed.
 */
export const approveArtifact = async ({
  studyId,
  artifactId,
  version,
  acceptUncertainCoverage = false,
}: {
  studyId: string;
  artifactId: string;
  version: number;
  /**
   * The guide's "Approve anyway": the researcher has been shown the research questions the coverage
   * check was not sure the guide answers. Sent only then, and ignored for every other kind.
   */
  acceptUncertainCoverage?: boolean;
}): Promise<{ status: ArtifactStatus; turnId: string | null }> =>
  request(
    `/studies/${studyId}/artifacts/${artifactId}/approve`,
    // The turn the approval started, for the chat to attach to. Null is the ordinary answer when
    // the agent was already mid-reply — the approval landed; there is just nothing new to watch.
    z.object({ status: artifactStatusSchema, turnId: z.uuid().nullable() }),
    {
      method: 'POST',
      body: acceptUncertainCoverage ? { version, accept_uncertain_coverage: true } : { version },
    },
  );

/**
 * A full-document save, which always says which version it was editing — the one this UI
 * actually rendered, not the newest one it could go and fetch. Refetching to satisfy the
 * precondition would defeat it: the point is to refuse a save assembled from a document that has
 * since moved, and a caller that re-reads first is asserting about a document it never showed.
 */
export const saveArtifact = async ({
  studyId,
  kind,
  content,
  expectedVersion,
}: {
  studyId: string;
  kind: ArtifactKind;
  content: unknown;
  expectedVersion: number;
}): Promise<WriteResult> =>
  request(`/studies/${studyId}/artifacts/kind/${kind}`, writeResultSchema, {
    method: 'PUT',
    body: { content, expectedVersion },
  });

/**
 * One image the Guide step can show: every one the current Materials mark to show (`candidate`),
 * in the order they were chosen, then any other file the guide shows, each with the question it is
 * shown before (`shown_on`), or `null` while no question shows it.
 */
const guideImageSchema = z.object({
  file_id: z.uuid(),
  name: z.string(),
  media_type: z.enum(STUDY_FILE_MEDIA_TYPES),
  width: z.int().positive().nullable(),
  height: z.int().positive().nullable(),
  candidate: z.boolean(),
  shown_on: z.uuid().nullable(),
});
export type GuideImage = z.infer<typeof guideImageSchema>;

/**
 * Everything the Guide step shows, as the server holds it: the guide and where it stands, every
 * version with what produced it (oldest first), what a reconciled current version changed and why,
 * the research questions it serves and whether they authorize a guide, the shape the study's template drafts to, the chosen Goal's minutes, the latest
 * drafting request and what the study would draft from now (null until the research questions
 * authorize a guide), the latest coverage check with the pair a check would judge now (null
 * while there is no guide or no research questions), and the images the step can show.
 */
const guideViewSchema = z.object({
  dg: z
    .object({
      artifactId: z.uuid(),
      version: z.int().positive(),
      status: artifactStatusSchema,
      approvedVersion: z.int().positive().nullable(),
      staleness: z.array(stalenessWindowSchema),
      content: dgContentSchema,
    })
    .nullable(),
  history: z.array(
    z.object({
      version: z.int().positive(),
      authoredBy: authorKindSchema,
      reason: z.string().nullable(),
      reasonText: z.string(),
      createdAt: z.iso.datetime(),
    }),
  ),
  /** What a reconciled current version changed and why; `null` for any other version. */
  reconciliation: reconciliationViewSchema.nullable(),
  rqs: z
    .object({
      version: z.int().positive(),
      authorizes: z.boolean(),
      items: z.array(z.object({ id: z.uuid(), text: z.string() })),
    })
    .nullable(),
  shape: guideShapeSchema,
  targetMinutes: z.number().nullable(),
  draft: dgDraftRequestSchema.nullable(),
  currentInputs: dgDraftInputsSchema.nullable(),
  coverage: dgCoverageRequestSchema.nullable(),
  coverageInputs: dgCoverageInputsSchema.nullable(),
  images: z.array(guideImageSchema),
});
export type GuideView = z.infer<typeof guideViewSchema>;

export const fetchGuide = async (studyId: string, signal?: AbortSignal): Promise<GuideView> =>
  request(`/studies/${studyId}/guide`, guideViewSchema, { signal });

/**
 * Asks for the guide to be drafted. Without `again` the server answers what it holds when nothing
 * needs drafting or a request for the study as it stands is in hand; `again` is "Try again" and
 * "Redraft the guide". Answers with the request as stored, or null when nothing is drafted.
 */
export const requestGuideDraft = async (
  studyId: string,
  { again = false }: { again?: boolean } = {},
): Promise<GuideView['draft']> =>
  (
    await request(
      `/studies/${studyId}/guide/draft`,
      z.object({ draft: dgDraftRequestSchema.nullable() }),
      { method: 'POST', body: again ? { again: true } : {} },
    )
  ).draft;

/**
 * Asks for the guide to be checked against its research questions. Without `again` the server
 * answers a check already in hand for the guide as it stands; `again` is "Try again" after a check
 * that found nothing to report.
 */
export const requestGuideCoverage = async (
  studyId: string,
  { again = false }: { again?: boolean } = {},
): Promise<GuideView['coverage']> =>
  (
    await request(
      `/studies/${studyId}/guide/coverage`,
      z.object({ coverage: dgCoverageRequestSchema.nullable() }),
      { method: 'POST', body: again ? { again: true } : {} },
    )
  ).coverage;

/**
 * Rewording one guide question — its text, and for a poll its options' text, the same options in
 * the same order — quoting the version of the guide it was made over: a guide that has moved since
 * is refused `artifact_version_not_current` rather than reworded under the user.
 */
export const rewordGuideQuestion = async ({
  studyId,
  questionId,
  text,
  options,
  expectedVersion,
  acknowledgedDownstream,
}: {
  studyId: string;
  questionId: string;
  text: string;
  options?: readonly { id: string; text: string }[];
  expectedVersion: number;
  acknowledgedDownstream: ChangeAcknowledgement | null;
}): Promise<{ version: number; deduped: boolean }> =>
  request(
    `/studies/${studyId}/guide/questions/${questionId}`,
    z.object({ version: z.int().positive(), deduped: z.boolean() }),
    {
      method: 'PATCH',
      body: {
        text,
        ...(options === undefined ? {} : { options }),
        expected_version: expectedVersion,
        acknowledged_downstream: acknowledgedDownstream,
      },
    },
  );

/**
 * Everything the Audience step shows, as the server holds it: the audience and where it stands,
 * every version with what produced it (oldest first), what a reconciled current version changed and
 * why, the count the study's template recommends and why, the Quality floor, whether the guide the
 * audience is built on is approved as it stands, the latest request to reconcile it and what the
 * study would reconcile it from now (null until there is an audience over an approved guide).
 */
const audienceViewSchema = z.object({
  audience: z
    .object({
      artifactId: z.uuid(),
      version: z.int().positive(),
      status: artifactStatusSchema,
      approvedVersion: z.int().positive().nullable(),
      staleReason: staleReasonSchema.nullable(),
      content: audienceContentSchema,
    })
    .nullable(),
  history: z.array(
    z.object({
      version: z.int().positive(),
      authoredBy: authorKindSchema,
      reason: z.string().nullable(),
      reasonText: z.string(),
      createdAt: z.iso.datetime(),
    }),
  ),
  /** What a reconciled current version changed and why; `null` for any other version. */
  reconciliation: reconciliationViewSchema.nullable(),
  recommended: z.object({ completes: z.int().positive(), rationale: z.string() }),
  floor: z.int().positive(),
  guideApproved: z.boolean(),
  draft: audienceDraftRequestSchema.nullable(),
  currentInputs: audienceDraftInputsSchema.nullable(),
});
export type AudienceView = z.infer<typeof audienceViewSchema>;

export const fetchAudience = async (studyId: string, signal?: AbortSignal): Promise<AudienceView> =>
  request(`/studies/${studyId}/audience`, audienceViewSchema, { signal });

/**
 * Asks for the audience to be reconciled with the changes above it. Without `again` the server
 * answers what it holds when nothing needs reconciling or a request for the study as it stands is
 * in hand; `again` is "Try again". Answers with the request as stored, or null when there is none.
 */
export const requestAudienceDraft = async (
  studyId: string,
  { again = false }: { again?: boolean } = {},
): Promise<AudienceView['draft']> =>
  (
    await request(
      `/studies/${studyId}/audience/draft`,
      z.object({ draft: audienceDraftRequestSchema.nullable() }),
      { method: 'POST', body: again ? { again: true } : {} },
    )
  ).draft;

/**
 * Rewording one screening question — its text, and its options' text, the same options in the same
 * order — quoting the version of the audience it was made over: an audience that has moved since is
 * refused `artifact_version_not_current` rather than reworded under the user.
 */
export const rewordAudienceScreener = async ({
  studyId,
  screenerId,
  text,
  options,
  expectedVersion,
}: {
  studyId: string;
  screenerId: string;
  text: string;
  options: readonly { id: string; text: string }[];
  expectedVersion: number;
}): Promise<{ version: number; deduped: boolean }> =>
  request(
    `/studies/${studyId}/audience/screeners/${screenerId}`,
    z.object({ version: z.int().positive(), deduped: z.boolean() }),
    { method: 'PATCH', body: { text, options, expected_version: expectedVersion } },
  );

/**
 * A started turn, and the id of the row the send wrote.
 *
 * The message id is what lets the optimistic copy in the chat and the stored row be recognised as
 * the same message. The client mints an id to render immediately and the database mints the row's
 * own, so without this the two are simply different messages — and a transcript read that lands
 * after the insert returns the row beside the copy already on screen.
 */
export interface StartedTurn {
  turnId: string;
  messageId?: string;
}

/**
 * Starts a turn and answers with its id and the id of the message the server persisted.
 *
 * The nesting happens here rather than in the caller so the transport above stays in terms of
 * text: the wire shape belongs to the agent-runtime ticket and is free to grow fields, and this
 * is the one place that has to follow it.
 */
export const startTurn = async ({
  studyId,
  step,
  message,
}: {
  /** The study this turn belongs to. */
  studyId: string;
  /** The step whose thread the turn and its message are written into. */
  step: ChatStepSlug;
  message?: string;
}): Promise<StartedTurn> =>
  request(
    `/studies/${studyId}/threads/${step}/turns`,
    // `messageId` is absent when the turn carries no message — a scripted turn, or a resume — so it
    // is optional here rather than defaulted: there is no row to name, and inventing an id would
    // give the caller something to reconcile against that does not exist.
    z.object({ turnId: z.uuid(), messageId: z.uuid().optional() }),
    {
      method: 'POST',
      body: message === undefined ? {} : { message: { text: message } },
    },
  );

export const stopTurn = async (turnId: string): Promise<void> => {
  await request(`/turns/${turnId}/stop`, z.object({ accepted: z.boolean(), status: z.string() }), {
    method: 'POST',
  });
};
